import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SupplementsAdminManager from '@/components/SupplementsAdminManager';
import CommunityAdminBlock from '@/components/CommunityAdminBlock';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, Legend, Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/personalized-plans/fit-evolution/admin`;
// Prefix distinto usado por los endpoints de plan MANUAL (admin/fit-evolution/...)
const MANUAL_API = `${BACKEND_URL}/api/personalized-plans/admin/fit-evolution`;

const BLOCK_COLORS = {
  1: '#22d3ee', // cyan Adaptación
  2: '#a78bfa', // violet Progresión
  3: '#f472b6', // pink Intensificación
};

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const publishNow = async (email) => {
  await axios.post(
    `${BACKEND_URL}/api/personalized-plans/admin/fit-evolution/publish-plan/${encodeURIComponent(email)}`,
    {},
    { headers: authHeaders() }
  );
};

export default function FitEvolutionAdminManager() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [rowMacros, setRowMacros] = useState({}); // {email: {kcal, p, c, f}} para edición inline

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/overview`, { headers: authHeaders() });
      setOverview(res.data);
    } catch (e) {
      toast.error('Error cargando el resumen Fit Evolution');
    } finally {
      setLoading(false);
    }
  };

  const [maintBusy, setMaintBusy] = useState(false);

  const runRegenerateAllRecipes = async () => {
    const ok = window.confirm(
      '¿Regenerar las recetas de TODAS las clientas activas de Fit Evolution?\n\n' +
      'Se eliminarán las recetas anteriores y se dispararán generaciones nuevas ' +
      'en segundo plano. Esta acción puede tardar varios minutos.'
    );
    if (!ok) return;
    setMaintBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/fit-evolution/regenerate-recipes-all-active`,
        {},
        { headers: authHeaders() }
      );
      const n = res?.data?.dispatched ?? 0;
      const t = res?.data?.total_basic_users ?? 0;
      toast.success(`Regeneración disparada para ${n} de ${t} clientas.`);
    } catch (e) {
      toast.error('No se pudo disparar la regeneración masiva.');
    } finally {
      setMaintBusy(false);
    }
  };

  const runMacrosMigration = async () => {
    try {
      const pre = await axios.post(`${MANUAL_API}/migrate-recompute-macros?dry_run=true`, {}, { headers: authHeaders() });
      if (pre.data.already_applied) {
        toast.info('Esta actualización de macros ya se había aplicado antes.');
        return;
      }
      if (!pre.data.users_updated) {
        toast.info('No hay clientas con plan generado y datos completos para recalcular.');
        return;
      }
      const ok = window.confirm(
        `¿Recalcular calorías y macros de ${pre.data.users_updated} clientas (${pre.data.batches_updated} lotes de recetas) con la fórmula nueva?\n\n` +
        'Se sobrescriben las calorías y gramos objetivo guardados. Esta acción solo se puede ejecutar una vez.'
      );
      if (!ok) return;
      setMaintBusy(true);
      const res = await axios.post(`${MANUAL_API}/migrate-recompute-macros`, {}, { headers: authHeaders() });
      if (res.data.already_applied) {
        toast.info('Esta actualización de macros ya se había aplicado antes.');
      } else {
        const sk = res.data.skipped?.length || 0;
        toast.success(`Macros recalculados: ${res.data.users_updated} clientas, ${res.data.batches_updated} lotes.${sk ? ` ${sk} omitidas por datos incompletos.` : ''}`);
        fetchOverview();
      }
    } catch (e) {
      toast.error('No se pudo recalcular los macros.');
    } finally {
      setMaintBusy(false);
    }
  };

  const runBackfillMainIngredients = async () => {
    const ok = window.confirm(
      '¿Recalcular los ingredientes principales de TODAS las recetas de la biblioteca?\n\n' +
      'Se reclasificarán con las reglas actuales (jugos y zumos quedan excluidos).'
    );
    if (!ok) return;
    setMaintBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/recipe-library/backfill-main-ingredients`,
        {},
        { headers: authHeaders() }
      );
      const upd = res?.data?.updated ?? 0;
      const tot = res?.data?.total ?? 0;
      toast.success(`Ingredientes recalculados: ${upd} recetas actualizadas de ${tot}.`);
    } catch (e) {
      toast.error('No se pudo recalcular los ingredientes principales.');
    } finally {
      setMaintBusy(false);
    }
  };

  const fetchDetail = async (email) => {
    setDetailLoading(true);
    setSelectedEmail(email);
    try {
      const res = await axios.get(`${API}/user-detail/${encodeURIComponent(email)}`, { headers: authHeaders() });
      setDetail(res.data);
    } catch (e) {
      toast.error('No se pudo cargar el detalle de esta clienta');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  const filteredUsers = useMemo(() => {
    if (!overview?.users) return [];
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return overview.users;
    return overview.users.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [overview, searchQuery]);

  const saveUserMacros = async (email, patch) => {
    try {
      const res = await axios.patch(
        `${MANUAL_API}/user-macros/${encodeURIComponent(email)}`,
        patch,
        { headers: authHeaders() }
      );
      toast.success(res.data?.plan_rescale?.batches > 0 ? 'Macros actualizados y plan ajustado (mismos alimentos)' : 'Macros actualizados');
      // Actualizar en la lista local sin re-fetch
      setOverview((prev) => prev && ({
        ...prev,
        users: prev.users.map((u) => u.email === email ? {
          ...u,
          target_calories: res.data.target_calories ?? u.target_calories,
          target_macros_grams: res.data.target_macros_grams ?? u.target_macros_grams,
          diet_macros: res.data.diet_macros ?? u.diet_macros,
        } : u),
      }));
      // Si estamos viendo detalle de este usuario, refrescar también
      if (selectedEmail === email && detail) {
        setDetail((prev) => prev && ({
          ...prev,
          user: {
            ...prev.user,
            target_calories: res.data.target_calories ?? prev.user.target_calories,
            target_macros_grams: res.data.target_macros_grams ?? prev.user.target_macros_grams,
            diet_macros: res.data.diet_macros ?? prev.user.diet_macros,
          },
        }));
      }
      // Limpiar el buffer de edición inline
      setRowMacros((prev) => { const { [email]: _, ...rest } = prev; return rest; });
    } catch (e) {
      toast.error('No se pudo actualizar los macros');
    }
  };

  const stats = useMemo(() => {
    if (!overview?.users) return { total: 0, completed: 0, active: 0, block1: 0, block2: 0, block3: 0 };
    const s = { total: overview.users.length, completed: 0, active: 0, block1: 0, block2: 0, block3: 0 };
    overview.users.forEach(u => {
      if (u.completed) s.completed += 1; else s.active += 1;
      if (u.block_number === 1) s.block1 += 1;
      else if (u.block_number === 2) s.block2 += 1;
      else if (u.block_number === 3) s.block3 += 1;
    });
    return s;
  }, [overview]);

  const blockDistribution = useMemo(() => ([
    { block: 'Adaptación', usuarias: stats.block1, fill: BLOCK_COLORS[1] },
    { block: 'Progresión', usuarias: stats.block2, fill: BLOCK_COLORS[2] },
    { block: 'Intensificación', usuarias: stats.block3, fill: BLOCK_COLORS[3] },
  ]), [stats]);

  const weightChart = useMemo(() => {
    if (!detail?.weight_series) return [];
    return detail.weight_series.map(pt => ({
      date: fmtDate(pt.date),
      peso: Number(pt.weight),
    }));
  }, [detail]);

  return (
    <div className="space-y-6" data-testid="fit-evolution-admin-panel">
      {/* Sección independiente de comunidad (WhatsApp / Telegram) */}
      <CommunityAdminBlock />
      {/* Panel de suplementación (independiente del filtro por clienta) */}
      <SupplementsAdminManager />

      <Card style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(var(--brand-primary-glow),0.3)' }}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-white">🚀 Fit Evolution — Progreso Individual</CardTitle>
              <CardDescription className="text-gray-400">
                Seguimiento del tracker de 12 semanas por clienta con dieta, semana actual, bloque y peso.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                onClick={runMacrosMigration}
                variant="outline"
                size="sm"
                disabled={maintBusy}
                data-testid="fe-admin-migrate-macros-btn"
                title="Recalcula una sola vez las calorías y macros de todas las clientas con plan generado"
              >
                Recalcular macros (fórmula nueva)
              </Button>
              <Button
                onClick={runBackfillMainIngredients}
                variant="outline"
                size="sm"
                disabled={maintBusy}
                data-testid="fe-admin-recalc-main-ingredients-btn"
                title="Reclasifica los ingredientes principales de todas las recetas de la biblioteca"
              >
                🧮 Recalcular ingredientes principales
              </Button>
              <Button
                onClick={runRegenerateAllRecipes}
                variant="outline"
                size="sm"
                disabled={maintBusy}
                data-testid="fe-admin-regen-all-recipes-btn"
                title="Regenera las recetas de todas las clientas activas de Fit Evolution"
              >
                🍽️ Regenerar recetas (todas)
              </Button>
              <Button
                onClick={fetchOverview}
                variant="outline"
                size="sm"
                data-testid="fe-admin-refresh-btn"
              >
                🔄 Actualizar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-400">Cargando...</p>
            </div>
          ) : !overview || overview.total === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-6xl mb-4">📊</p>
              <p className="text-lg text-gray-300">No hay clientas Fit Evolution activas todavía</p>
              <p className="text-sm text-gray-500 mt-2">
                Aparecerán aquí cuando su plan de 24h sea publicado.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #b91c6a)' }} data-testid="fe-kpi-total">
                  <p className="text-xs text-white/70 uppercase tracking-wider">Total</p>
                  <p className="text-3xl font-bold text-white">{stats.total}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, #22d3ee, #0e7490)' }} data-testid="fe-kpi-active">
                  <p className="text-xs text-white/70 uppercase tracking-wider">Activas</p>
                  <p className="text-3xl font-bold text-white">{stats.active}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, #10b981, #065f46)' }} data-testid="fe-kpi-completed">
                  <p className="text-xs text-white/70 uppercase tracking-wider">Ciclos 12sem completos</p>
                  <p className="text-3xl font-bold text-white">{stats.completed}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, #f472b6, #831843)' }} data-testid="fe-kpi-intense">
                  <p className="text-xs text-white/70 uppercase tracking-wider">En Intensificación</p>
                  <p className="text-3xl font-bold text-white">{stats.block3}</p>
                </div>
              </div>

              {/* Gráfico distribución bloques */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                <p className="text-sm text-gray-300 mb-3 font-semibold">Distribución por bloque de progresión</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={blockDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="block" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#111', border: '1px solid #333', borderRadius: 8 }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="usuarias" radius={[6, 6, 0, 0]}>
                      {blockDistribution.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Tabla clientas */}
              <div className="rounded-lg" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                {/* Búsqueda */}
                <div className="p-3 border-b border-white/5 flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="🔍 Buscar por nombre o email…"
                    className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder-white/40 focus:border-pink-400 focus:outline-none"
                    data-testid="fe-admin-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="px-2 py-1 rounded text-xs bg-white/5 hover:bg-white/10 text-white/70"
                      data-testid="fe-admin-search-clear"
                    >✕</button>
                  )}
                  <span className="text-xs text-white/50" data-testid="fe-admin-search-count">
                    {filteredUsers.length} / {overview.users.length}
                  </span>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fe-admin-users-table">
                  <thead style={{ backgroundColor: 'rgba(var(--brand-primary-glow),0.15)' }}>
                    <tr className="text-left text-gray-300">
                      <th className="px-4 py-3">Clienta</th>
                      <th className="px-4 py-3">Dieta</th>
                      <th className="px-4 py-3">🔥 kcal</th>
                      <th className="px-4 py-3">Macros (g)</th>
                      <th className="px-4 py-3">Semana</th>
                      <th className="px-4 py-3">Bloque</th>
                      <th className="px-4 py-3">Updates</th>
                      <th className="px-4 py-3">Último peso</th>
                      <th className="px-4 py-3">Último login</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => {
                      const buf = rowMacros[u.email] || {};
                      const kcal = buf.kcal ?? u.target_calories ?? '';
                      const p = buf.p ?? u.target_macros_grams?.protein ?? '';
                      const c = buf.c ?? u.target_macros_grams?.carbs ?? '';
                      const f = buf.f ?? u.target_macros_grams?.fats ?? '';
                      const dirty = buf.kcal !== undefined || buf.p !== undefined || buf.c !== undefined || buf.f !== undefined;
                      return (
                      <tr
                        key={u.email}
                        className="border-t hover:bg-white/5"
                        style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                        data-testid={`fe-admin-row-${u.email}`}
                      >
                        <td className="px-4 py-3 text-white cursor-pointer" onClick={() => fetchDetail(u.email)}>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-gray-400">{u.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-300 cursor-pointer" onClick={() => fetchDetail(u.email)}>{u.diet_type || '—'}</td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={kcal}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setRowMacros((prev) => ({ ...prev, [u.email]: { ...(prev[u.email] || {}), kcal: e.target.value } }))}
                            placeholder="kcal"
                            className="w-20 px-2 py-1 rounded bg-black/40 border border-white/10 text-white text-xs focus:border-emerald-400 focus:outline-none"
                            data-testid={`fe-admin-kcal-${u.email}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {[
                              { k: 'p', label: 'P', color: 'text-blue-300' },
                              { k: 'c', label: 'C', color: 'text-yellow-300' },
                              { k: 'f', label: 'G', color: 'text-green-300' },
                            ].map((cfg) => (
                              <label key={cfg.k} className={`text-[10px] font-bold ${cfg.color}`}>
                                {cfg.label}
                                <input
                                  type="number"
                                  value={cfg.k === 'p' ? p : cfg.k === 'c' ? c : f}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setRowMacros((prev) => {
                                    // kcal = P*4 + C*4 + G*9 al editar cualquier macro
                                    const cur = { ...(prev[u.email] || {}), [cfg.k]: e.target.value };
                                    const g = (k, base) => Number(cur[k] ?? base ?? 0) || 0;
                                    cur.kcal = String(Math.round(
                                      g('p', u.target_macros_grams?.protein) * 4 +
                                      g('c', u.target_macros_grams?.carbs) * 4 +
                                      g('f', u.target_macros_grams?.fats) * 9
                                    ));
                                    return { ...prev, [u.email]: cur };
                                  })}
                                  placeholder="0"
                                  className="ml-1 w-14 px-1.5 py-1 rounded bg-black/40 border border-white/10 text-white text-xs font-normal"
                                  data-testid={`fe-admin-macro-${cfg.k}-${u.email}`}
                                />
                              </label>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 cursor-pointer" onClick={() => fetchDetail(u.email)}>
                          <span className="text-white font-semibold">{u.current_week}</span>
                          <span className="text-gray-500 text-xs">/12</span>
                        </td>
                        <td className="px-4 py-3 cursor-pointer" onClick={() => fetchDetail(u.email)}>
                          <Badge
                            style={{ backgroundColor: BLOCK_COLORS[u.block_number] || '#666', color: '#000' }}
                          >
                            {u.block_label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-300 cursor-pointer" onClick={() => fetchDetail(u.email)}>{u.updates_count}</td>
                        <td className="px-4 py-3 text-gray-300 cursor-pointer" onClick={() => fetchDetail(u.email)}>{u.last_weight ? `${u.last_weight} kg` : '—'}</td>
                        <td
                          className="px-4 py-3 text-gray-300 cursor-pointer whitespace-nowrap"
                          onClick={() => fetchDetail(u.email)}
                          data-testid={`fe-admin-last-login-${u.email}`}
                        >
                          {u.last_login_at ? (
                            <div>
                              <div className="text-white text-xs font-semibold">
                                {new Date(u.last_login_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {new Date(u.last_login_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                {(() => {
                                  const diffMs = Date.now() - new Date(u.last_login_at).getTime();
                                  const days = Math.floor(diffMs / 86400000);
                                  if (days <= 0) return ' · hoy';
                                  if (days === 1) return ' · ayer';
                                  return ` · hace ${days} d`;
                                })()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {dirty ? (
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                const patch = {};
                                if (buf.kcal !== undefined && buf.kcal !== '') patch.target_calories = Number(buf.kcal);
                                if (buf.p !== undefined && buf.p !== '') patch.protein_g = Number(buf.p);
                                if (buf.c !== undefined && buf.c !== '') patch.carbs_g = Number(buf.c);
                                if (buf.f !== undefined && buf.f !== '') patch.fats_g = Number(buf.f);
                                saveUserMacros(u.email, patch);
                              }}
                              data-testid={`fe-admin-save-macros-${u.email}`}
                            >
                              💾 Guardar
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); fetchDetail(u.email); }}
                              data-testid={`fe-admin-view-${u.email}`}
                            >
                              Ver detalle
                            </Button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DETALLE */}
      {selectedEmail && (
        <Card style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,240,255,0.3)' }} data-testid="fe-admin-detail-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-white">
                  📈 Detalle · {detail?.user?.name || selectedEmail}
                </CardTitle>
                <CardDescription className="text-gray-400">{selectedEmail}</CardDescription>
              </div>
              <div className="flex gap-2">
                {detail?.user && !detail.user.published_at && detail.user.scheduled_at && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await publishNow(selectedEmail);
                        toast.success('Plan publicado 🚀');
                        await fetchOverview();
                        await fetchDetail(selectedEmail);
                      } catch { toast.error('No se pudo publicar'); }
                    }}
                    data-testid="fe-admin-publish-now"
                  >
                    🚀 Publicar ahora
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setSelectedEmail(null); setDetail(null); }}
                  data-testid="fe-admin-close-detail"
                >
                  ✕ Cerrar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {detailLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : !detail ? (
              <p className="text-gray-500 text-center py-8">Sin datos</p>
            ) : (
              <div className="space-y-6">
                {/* ═══ ETIQUETA ADMIN + MODO MANUAL (solo admin) ═══ */}
                <AdminLabelSection
                  email={selectedEmail}
                  initialLabel={detail.user.admin_label || ''}
                  initialManualMode={!!detail.user.manual_mode}
                  onSaved={() => fetchDetail(selectedEmail)}
                />

                {/* Modo manual: gestor de comidas asignadas */}
                {detail.user.manual_mode && (
                  <ManualPlanSection
                    email={selectedEmail}
                    targetCalories={detail.user?.target_calories}
                    targetMacros={detail.user?.target_macros_grams}
                  />
                )}

                {/* Datos base */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} data-testid="fe-admin-diet-card">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Dieta</p>
                    <p className="text-lg text-white">{detail.user.diet_type || '—'}</p>
                    {detail.user.diet_macros && (
                      <p className="text-xs text-gray-400 mt-1">
                        P{detail.user.diet_macros.protein}% · C{detail.user.diet_macros.carbs}% · G{detail.user.diet_macros.fats}%
                      </p>
                    )}
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(52,211,153,0.10)' }} data-testid="fe-admin-kcal-card">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Calorías objetivo</p>
                    <p className="text-2xl text-white font-bold">
                      {detail.user.target_calories ? `${detail.user.target_calories}` : '—'}
                      <span className="text-sm text-gray-500 ml-1">kcal</span>
                    </p>
                    {detail.user.update_pending_release && detail.user.pending_target_calories && (
                      <p className="text-xs text-emerald-300 mt-1" data-testid="fe-admin-pending-kcal">
                        ⏳ Próximo: {detail.user.pending_target_calories} kcal (en 24h)
                      </p>
                    )}
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(var(--brand-primary-glow),0.08)' }}>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Semana actual</p>
                    <p className="text-2xl text-white font-bold">
                      {detail.progression?.current_week ?? '—'}
                      <span className="text-sm text-gray-500">/12</span>
                    </p>
                    <p className="text-xs text-gray-300 mt-1">{detail.progression?.block_label || ''}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(167,139,250,0.08)' }}>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Actualizaciones</p>
                    <p className="text-2xl text-white font-bold">{detail.updates_total}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Último peso: {detail.user.last_weight ? `${detail.user.last_weight} kg` : '—'}
                    </p>
                  </div>
                </div>

                {/* Último ajuste calculado por el sistema */}
                {detail.user.last_adjustment && (detail.user.last_adjustment.reason || (detail.user.last_adjustment.recommendations || []).length > 0) && (
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.30)' }}
                    data-testid="fe-admin-last-adjustment"
                  >
                    <p className="text-sm text-emerald-300 font-semibold mb-2">
                      🎯 Último ajuste automático
                      {detail.user.update_pending_release && (
                        <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-200 px-2 py-0.5 rounded-full">
                          Pendiente de entrega (24h)
                        </span>
                      )}
                    </p>
                    {detail.user.last_adjustment.reason && (
                      <p className="text-sm text-white/90 mb-2">
                        <strong>Motivo:</strong> {detail.user.last_adjustment.reason}
                      </p>
                    )}
                    {(detail.user.last_adjustment.calorie_delta_pct !== 0 || detail.user.last_adjustment.new_target_calories) && (
                      <div className="text-xs text-gray-300 mb-2 flex flex-wrap gap-3">
                        {detail.user.last_adjustment.calorie_delta_pct !== 0 && (
                          <span>Δ kcal: <strong className={detail.user.last_adjustment.calorie_delta_pct > 0 ? 'text-emerald-300' : 'text-orange-300'}>
                            {detail.user.last_adjustment.calorie_delta_pct > 0 ? '+' : ''}{detail.user.last_adjustment.calorie_delta_pct}%
                          </strong></span>
                        )}
                        {detail.user.last_adjustment.new_target_calories && (
                          <span>Nuevas kcal: <strong className="text-white">{detail.user.last_adjustment.new_target_calories}</strong></span>
                        )}
                        {detail.user.last_adjustment.new_macros && (
                          <span>Nuevos macros: <strong className="text-white">
                            P{detail.user.last_adjustment.new_macros.protein}% · C{detail.user.last_adjustment.new_macros.carbs}% · G{detail.user.last_adjustment.new_macros.fats}%
                          </strong></span>
                        )}
                      </div>
                    )}
                    {(detail.user.last_adjustment.recommendations || []).length > 0 && (
                      <ul className="text-xs text-white/80 space-y-1 list-disc pl-4">
                        {detail.user.last_adjustment.recommendations.map((rec, i) => (
                          <li key={i} data-testid={`fe-admin-recommendation-${i}`}>{rec}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Gráfico peso */}
                {weightChart.length > 1 ? (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                    <p className="text-sm text-gray-300 mb-3 font-semibold">Evolución del peso (kg)</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={weightChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                        <YAxis stroke="#9ca3af" domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#111', border: '1px solid #333', borderRadius: 8 }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Legend wrapperStyle={{ color: '#9ca3af' }} />
                        <Line type="monotone" dataKey="peso" stroke="var(--brand-primary)" strokeWidth={3} dot={{ r: 5, fill: 'var(--brand-primary)' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                    <p className="text-gray-500 text-sm">
                      Necesita al menos 2 actualizaciones para graficar el peso. Actual: {weightChart.length}
                    </p>
                  </div>
                )}

                {/* Historial de actualizaciones */}
                <div>
                  <p className="text-sm text-gray-300 mb-3 font-semibold">Historial de actualizaciones</p>
                  {detail.updates.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aún sin actualizaciones enviadas.</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                      {[...detail.updates].reverse().map((up) => (
                        <div
                          key={up.id}
                          className="p-3 rounded-lg text-sm"
                          style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.05)' }}
                          data-testid={`fe-admin-update-${up.id}`}
                        >
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1">
                              <p className="text-white font-medium">{fmtDate(up.created_at)}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                Peso: <span className="text-white">{up.weight ?? '—'} kg</span>
                                {up.adherence_level && (<> · Adherencia: <span className="text-white">{up.adherence_level}</span></>)}
                              </p>
                              {up.notes && <p className="text-xs text-gray-300 mt-2 italic">&ldquo;{up.notes}&rdquo;</p>}
                              {up.adjustments && (up.adjustments.reason || up.adjustments.new_target_calories || (up.adjustments.recommendations || []).length > 0) && (
                                <div
                                  className="mt-2 pt-2 border-t border-white/10 text-xs"
                                  data-testid={`fe-admin-update-adjustments-${up.id}`}
                                >
                                  {up.adjustments.reason && (
                                    <p className="text-emerald-200 mb-1">🎯 {up.adjustments.reason}</p>
                                  )}
                                  <div className="flex flex-wrap gap-3 text-gray-300">
                                    {typeof up.adjustments.calorie_delta_pct === 'number' && up.adjustments.calorie_delta_pct !== 0 && (
                                      <span>Δ kcal: <strong className={up.adjustments.calorie_delta_pct > 0 ? 'text-emerald-300' : 'text-orange-300'}>
                                        {up.adjustments.calorie_delta_pct > 0 ? '+' : ''}{up.adjustments.calorie_delta_pct}%
                                      </strong></span>
                                    )}
                                    {up.adjustments.new_target_calories && (
                                      <span>Nuevas kcal: <strong className="text-white">{up.adjustments.new_target_calories}</strong></span>
                                    )}
                                    {up.adjustments.new_macros && (
                                      <span>Macros: <strong className="text-white">
                                        P{up.adjustments.new_macros.protein}% · C{up.adjustments.new_macros.carbs}% · G{up.adjustments.new_macros.fats}%
                                      </strong></span>
                                    )}
                                  </div>
                                  {(up.adjustments.recommendations || []).length > 0 && (
                                    <ul className="text-white/75 space-y-0.5 list-disc pl-4 mt-1.5">
                                      {up.adjustments.recommendations.map((rec, i) => (
                                        <li key={i}>{rec}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                            </div>
                            {up.objective_changed && (
                              <Badge className="bg-amber-500 text-black">Cambio de objetivo</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// AdminLabelSection: input de "Etiqueta admin" (interno) + toggle Modo Manual.
// Sólo se renderiza dentro del panel admin.
// ═════════════════════════════════════════════════════════════════════════
function AdminLabelSection({ email, initialLabel, initialManualMode, onSaved }) {
  const [label, setLabel] = useState(initialLabel || '');
  const [manualMode, setManualMode] = useState(!!initialManualMode);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLabel(initialLabel || '');
    setManualMode(!!initialManualMode);
  }, [initialLabel, initialManualMode]);

  const save = async () => {
    setSaving(true);
    try {
      // Ruta correcta del backend: /admin/fit-evolution/user-label/{email}
      // (NO /fit-evolution/admin/user-label — orden invertido).
      await axios.patch(
        `${BACKEND_URL}/api/personalized-plans/admin/fit-evolution/user-label/${encodeURIComponent(email)}`,
        { admin_label: label, manual_mode: manualMode },
        { headers: authHeaders() }
      );
      toast.success('Etiqueta guardada');
      onSaved && onSaved();
    } catch (err) {
      // Mostrar el error HTTP real (status + detail) en vez del genérico.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      toast.error(
        status
          ? `Error ${status}: ${detail || 'no se pudo guardar la etiqueta'}`
          : `No se pudo guardar: ${detail || 'error desconocido'}`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-4 rounded-xl"
      style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.35)' }}
      data-testid="fe-admin-label-section"
    >
      <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold mb-2">
        🔒 Etiqueta admin (interna — no visible para la clienta)
      </p>
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej: alumna asesoría personalizada premium"
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm placeholder-white/40 focus:border-purple-400 focus:outline-none"
            data-testid="fe-admin-label-input"
          />
        </div>
        <label className="flex items-center gap-2 text-white/80 text-sm shrink-0 cursor-pointer">
          <input
            type="checkbox"
            checked={manualMode}
            onChange={(e) => setManualMode(e.target.checked)}
            className="w-4 h-4 accent-purple-500"
            data-testid="fe-admin-manual-mode-toggle"
          />
          Modo manual
        </label>
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-purple-500 hover:bg-purple-600"
          data-testid="fe-admin-label-save"
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// ManualMacrosTracker: banner que muestra los macros consumidos vs objetivo
// contando SOLO la primera opción (meal_position=1) de cada comida — las
// demás opciones son alternativas y NO se suman al total del día.
// ═════════════════════════════════════════════════════════════════════════
function ManualMacrosTracker({ groups, targetCalories, targetMacros }) {
  const totals = useMemo(() => {
    let p = 0, c = 0, f = 0, k = 0;
    (groups || []).forEach((g) => {
      // Solo la opción con menor meal_position (o la primera del array) cuenta
      const opts = g.options || [];
      if (opts.length === 0) return;
      const primary = opts.reduce((min, o) =>
        (o.meal_position ?? 99) < (min.meal_position ?? 99) ? o : min, opts[0]);
      const r = primary.recipes?.[0] || {};
      const mp = primary.target_macros || {};
      p += Number(mp.protein ?? r.protein ?? 0);
      c += Number(mp.carbs ?? r.carbs ?? 0);
      f += Number(mp.fats ?? r.fats ?? 0);
      k += Number(primary.target_calories ?? r.calories ?? 0);
    });
    return { p: Math.round(p), c: Math.round(c), f: Math.round(f), k: Math.round(k) };
  }, [groups]);

  const target = {
    p: Number(targetMacros?.protein) || 0,
    c: Number(targetMacros?.carbs) || 0,
    f: Number(targetMacros?.fats) || 0,
    k: Number(targetCalories) || 0,
  };

  const remaining = {
    p: target.p - totals.p,
    c: target.c - totals.c,
    f: target.f - totals.f,
    k: target.k - totals.k,
  };

  const badge = (label, used, tgt, rem, colorClass) => {
    const over = tgt > 0 && used > tgt * 1.02;
    const exact = tgt > 0 && used >= tgt * 0.98 && used <= tgt * 1.02;
    const bg = over ? 'bg-red-500/15 border-red-400/40' : exact ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-slate-800/60 border-white/10';
    return (
      <div className={`flex-1 min-w-[100px] rounded-lg border-2 p-2 ${bg}`}>
        <div className={`text-[10px] font-black uppercase tracking-widest ${colorClass}`}>{label}</div>
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className={`text-lg font-black ${colorClass}`}>{used}</span>
          {tgt > 0 && <span className="text-[11px] text-slate-400">/ {tgt}</span>}
        </div>
        {tgt > 0 && (
          <div className={`text-[10px] font-semibold mt-0.5 ${rem >= 0 ? 'text-cyan-300' : 'text-red-300'}`}>
            {rem >= 0 ? `Faltan ${rem}` : `Te pasaste ${Math.abs(rem)}`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="rounded-lg border-2 border-fuchsia-500/40 p-3"
      style={{ background: 'linear-gradient(135deg, rgba(217,70,239,0.08), rgba(6,182,212,0.08))' }}
      data-testid="fe-admin-manual-macros-tracker"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-[11px] uppercase tracking-widest font-black text-fuchsia-300">
          📊 Total del día (solo cuenta 1ª opción de cada comida)
        </p>
        {target.k > 0 && (
          <span className="text-[10px] bg-white/10 text-white/80 px-2 py-1 rounded-full font-semibold">
            Objetivo: {target.k} kcal · P{target.p} · C{target.c} · G{target.f}
          </span>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {badge('Proteína', totals.p, target.p, remaining.p, 'text-blue-300')}
        {badge('Carbos', totals.c, target.c, remaining.c, 'text-yellow-300')}
        {badge('Grasas', totals.f, target.f, remaining.f, 'text-green-300')}
        {badge('Calorías', totals.k, target.k, remaining.k, 'text-purple-300')}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// ManualPlanSection: cuando manual_mode=true, permite al admin asignar
// recetas específicas de la biblioteca por comida y editar macros/kcal.
// - Múltiples opciones por meal_time (Comida 1 puede tener 3 recetas).
// - Botón "Vista previa" que muestra el plan EXACTO como lo ve la usuaria.
// Las recetas se muestran EXACTAMENTE como se ingresen (sin escalado).
// ═════════════════════════════════════════════════════════════════════════
function ManualPlanSection({ email, targetCalories, targetMacros }) {
  const [groups, setGroups] = useState([]);  // [{meal_time, options: [...]}]
  const [loading, setLoading] = useState(true);
  const [libResults, setLibResults] = useState([]);
  // showPicker = {mealTime, addOption: bool, replaceOptionId?}
  const [showPicker, setShowPicker] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [newMealTime, setNewMealTime] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${MANUAL_API}/manual-plan/${encodeURIComponent(email)}`,
        { headers: authHeaders() }
      );
      setGroups(res.data?.groups || []);
    } catch {
      toast.error('No se pudo cargar el plan manual');
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const res = await axios.get(
        `${MANUAL_API}/manual-plan-preview/${encodeURIComponent(email)}`,
        { headers: authHeaders() }
      );
      setPreview(res.data?.batches || []);
    } catch {
      setPreview([]);
    }
  };

  useEffect(() => { if (email) load(); }, [email]);
  useEffect(() => { if (showPreview) loadPreview(); }, [showPreview]);

  const searchLibrary = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/recipe-library`,
        { headers: authHeaders(), params: { search, limit: 30 } }
      );
      setLibResults(res.data?.items || []);
    } catch {
      setLibResults([]);
    }
  };

  const assignRecipe = async (recipe) => {
    if (!showPicker) return;
    try {
      const body = {
        meal_time: showPicker.mealTime,
        recipe_id: recipe.id,
      };
      // Si estamos reemplazando una opción específica (Cambiar), enviamos su id
      if (showPicker.replaceOptionId) body.id = showPicker.replaceOptionId;
      await axios.put(
        `${MANUAL_API}/manual-plan/${encodeURIComponent(email)}`,
        body,
        { headers: authHeaders() }
      );
      toast.success(`${recipe.name} asignado a ${showPicker.mealTime}`);
      setShowPicker(null);
      setSearch('');
      setLibResults([]);
      await load();
      if (showPreview) await loadPreview();
    } catch {
      toast.error('No se pudo asignar la receta');
    }
  };

  const removeOption = async (mealTime, optionId) => {
    if (!window.confirm('¿Eliminar esta opción?')) return;
    try {
      await axios.delete(
        `${MANUAL_API}/manual-plan/${encodeURIComponent(email)}/${encodeURIComponent(mealTime)}?id=${encodeURIComponent(optionId)}`,
        { headers: authHeaders() }
      );
      toast.success('Opción eliminada');
      await load();
      if (showPreview) await loadPreview();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const removeAllOptions = async (mealTime) => {
    if (!window.confirm(`¿Eliminar TODAS las opciones de ${mealTime}?`)) return;
    try {
      await axios.delete(
        `${MANUAL_API}/manual-plan/${encodeURIComponent(email)}/${encodeURIComponent(mealTime)}`,
        { headers: authHeaders() }
      );
      toast.success(`${mealTime} eliminado`);
      await load();
      if (showPreview) await loadPreview();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const saveMacros = async () => {
    if (!editing) return;
    try {
      await axios.put(
        `${MANUAL_API}/manual-plan/${encodeURIComponent(email)}`,
        {
          id: editing.id,
          meal_time: editing.meal_time,
          meal_position: editing.meal_position,
          recipe_id: editing.recipes?.[0]?.id,
          target_calories: Number(editing.target_calories) || 0,
          target_macros: {
            protein: Number(editing.target_macros?.protein) || 0,
            carbs: Number(editing.target_macros?.carbs) || 0,
            fats: Number(editing.target_macros?.fats) || 0,
          },
        },
        { headers: authHeaders() }
      );
      toast.success('Macros actualizados');
      setEditing(null);
      await load();
      if (showPreview) await loadPreview();
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  const openPicker = (mealTime, replaceOptionId = null) => {
    setShowPicker({ mealTime, replaceOptionId });
    setSearch('');
    searchLibrary();
  };

  return (
    <div
      className="p-4 rounded-xl space-y-4"
      style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.35)' }}
      data-testid="fe-admin-manual-plan-section"
    >
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">
            🛠 Modo manual — plan de comidas
          </p>
          <p className="text-xs text-white/60 mt-0.5">
            La clienta verá SOLO estas comidas (reemplaza el plan automático). Puedes agregar varias opciones por tiempo de comida.
          </p>
        </div>
        <Button
          size="sm"
          variant={showPreview ? 'default' : 'outline'}
          onClick={() => setShowPreview((v) => !v)}
          className={showPreview ? 'bg-cyan-500 hover:bg-cyan-600' : ''}
          data-testid="fe-admin-manual-toggle-preview"
        >
          {showPreview ? '📋 Ocultar vista previa' : '👁 Ver como la clienta'}
        </Button>
      </div>

      {/* Tracker de macros restantes — solo cuenta la OPCIÓN 1 (position 1) de cada comida */}
      <ManualMacrosTracker
        groups={groups}
        targetCalories={targetCalories}
        targetMacros={targetMacros}
      />

      {/* Añadir nuevo tiempo de comida */}
      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={newMealTime}
          onChange={(e) => setNewMealTime(e.target.value)}
          placeholder="Ej: Comida 1 / Desayuno"
          className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm placeholder-white/40 focus:border-emerald-400 focus:outline-none"
          data-testid="fe-admin-manual-new-meal"
        />
        <Button
          size="sm"
          disabled={!newMealTime.trim()}
          onClick={() => { openPicker(newMealTime.trim()); setNewMealTime(''); }}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="fe-admin-manual-add-meal"
        >
          + Añadir comida
        </Button>
      </div>

      {/* Listado de comidas agrupadas por meal_time */}
      {loading ? (
        <p className="text-white/60 text-sm">Cargando plan manual…</p>
      ) : groups.length === 0 ? (
        <p className="text-white/60 text-sm">Aún no hay comidas asignadas. Añade la primera desde arriba.</p>
      ) : (
        <ul className="space-y-3">
          {groups.map((g) => (
            <li
              key={g.meal_time}
              className="p-3 rounded-lg bg-black/30 border border-white/10"
              data-testid={`fe-admin-manual-meal-${g.meal_time}`}
            >
              <div className="flex justify-between items-center mb-2 gap-2">
                <p className="text-emerald-300 font-bold text-sm">
                  🍽 {g.meal_time}
                  <span className="text-white/50 font-normal ml-2 text-xs">
                    ({g.options.length} {g.options.length === 1 ? 'opción' : 'opciones'})
                  </span>
                </p>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-emerald-400/60 text-emerald-300"
                    onClick={() => openPicker(g.meal_time)}
                    data-testid={`fe-admin-manual-add-option-${g.meal_time}`}
                  >
                    + Otra opción
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-red-400 border-red-400/40"
                    onClick={() => removeAllOptions(g.meal_time)}
                    data-testid={`fe-admin-manual-remove-all-${g.meal_time}`}
                  >
                    Borrar todo
                  </Button>
                </div>
              </div>

              <ul className="space-y-1.5">
                {g.options.map((m, idx) => {
                  const r = m.recipes?.[0] || {};
                  const isEditing = editing?.id === m.id;
                  return (
                    <li
                      key={m.id}
                      className="p-2 rounded bg-black/40 border border-white/5"
                      data-testid={`fe-admin-manual-option-${m.id}`}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white/80 text-xs">
                            Opción {idx + 1} · <span className="text-white font-medium">{r.name || 'Sin receta'}</span>
                          </p>
                          {!isEditing && (
                            <p className="text-xs text-white/50 mt-0.5">
                              {m.target_calories ? `${Math.round(m.target_calories)} kcal · ` : ''}
                              P {m.target_macros?.protein ?? 0}g · C {m.target_macros?.carbs ?? 0}g · G {m.target_macros?.fats ?? 0}g
                            </p>
                          )}
                          {isEditing && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                              <input type="number" value={editing.target_calories || ''} onChange={(e) => setEditing({ ...editing, target_calories: e.target.value })} placeholder="kcal" className="px-2 py-1 rounded bg-black/40 border border-white/15 text-white text-xs" />
                              <input type="number" value={editing.target_macros?.protein || ''} onChange={(e) => setEditing({ ...editing, target_macros: { ...(editing.target_macros || {}), protein: e.target.value } })} placeholder="Prot (g)" className="px-2 py-1 rounded bg-black/40 border border-white/15 text-white text-xs" />
                              <input type="number" value={editing.target_macros?.carbs || ''} onChange={(e) => setEditing({ ...editing, target_macros: { ...(editing.target_macros || {}), carbs: e.target.value } })} placeholder="Carbs (g)" className="px-2 py-1 rounded bg-black/40 border border-white/15 text-white text-xs" />
                              <input type="number" value={editing.target_macros?.fats || ''} onChange={(e) => setEditing({ ...editing, target_macros: { ...(editing.target_macros || {}), fats: e.target.value } })} placeholder="Grasas (g)" className="px-2 py-1 rounded bg-black/40 border border-white/15 text-white text-xs" />
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {!isEditing ? (
                            <>
                              <button
                                onClick={() => openPicker(g.meal_time, m.id)}
                                className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                                data-testid={`fe-admin-manual-change-option-${m.id}`}
                              >Cambiar</button>
                              <button
                                onClick={() => setEditing(m)}
                                className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                                data-testid={`fe-admin-manual-edit-option-${m.id}`}
                              >Macros</button>
                              <button
                                onClick={() => removeOption(g.meal_time, m.id)}
                                className="text-[10px] px-2 py-1 rounded bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30"
                                data-testid={`fe-admin-manual-delete-option-${m.id}`}
                              >✕</button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={saveMacros}
                                className="text-[10px] px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid={`fe-admin-manual-save-option-${m.id}`}
                              >Guardar</button>
                              <button
                                onClick={() => setEditing(null)}
                                className="text-[10px] px-2 py-1 rounded bg-white/5 text-white/70"
                              >Cancelar</button>
                            </>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {/* Vista previa como la clienta */}
      {showPreview && (
        <div
          className="rounded-lg p-3 mt-2"
          style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.35)' }}
          data-testid="fe-admin-manual-preview"
        >
          <p className="text-[11px] uppercase tracking-widest text-cyan-300 font-bold mb-2">
            👁 Vista previa — tal como la ve la clienta
          </p>
          {preview === null ? (
            <p className="text-white/60 text-sm">Cargando vista previa…</p>
          ) : preview.length === 0 ? (
            <p className="text-white/60 text-sm">Aún no hay comidas asignadas. Empieza por asignar la primera receta arriba.</p>
          ) : (
            <div className="space-y-2">
              {preview.map((b, i) => {
                const r = b.recipes?.[0] || {};
                return (
                  <div key={b.id || i} className="p-2 rounded bg-black/30 border border-white/5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-cyan-200 text-sm font-semibold">🍽 {b.meal_time}</p>
                      <span className="text-[10px] text-white/50">
                        {r.calories ? `${Math.round(r.calories)} kcal` : ''}
                      </span>
                    </div>
                    <p className="text-white/90 text-xs mt-1">{r.name}</p>
                    {(r.ingredients || []).length > 0 && (
                      <ul className="mt-1 text-[11px] text-white/60 list-disc pl-4 space-y-0.5">
                        {(r.ingredients || []).slice(0, 5).map((ing, j) => (
                          <li key={j}>
                            {typeof ing === 'string' ? ing : (
                              ing.name ? `${ing.grams ? ing.grams + 'g ' : ''}${ing.name}` : JSON.stringify(ing)
                            )}
                          </li>
                        ))}
                        {(r.ingredients || []).length > 5 && (
                          <li className="text-white/40">+ {r.ingredients.length - 5} más…</li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal / picker de recetas de biblioteca */}
      {showPicker && (
        <div
          className="p-3 rounded-lg mt-3"
          style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="fe-admin-manual-recipe-picker"
        >
          <p className="text-white text-sm font-semibold mb-2">
            {showPicker.replaceOptionId ? 'Cambiar receta de ' : 'Elegir receta para '}
            <span className="text-emerald-300">{showPicker.mealTime}</span>
          </p>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchLibrary()}
              placeholder="Buscar en la biblioteca…"
              className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm"
              data-testid="fe-admin-manual-search-input"
            />
            <Button size="sm" onClick={searchLibrary}>Buscar</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowPicker(null); setLibResults([]); }}>
              ✕
            </Button>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {libResults.length === 0 ? (
              <p className="text-white/50 text-xs">Sin resultados. Escribe algo y toca buscar.</p>
            ) : (
              libResults.map((r) => (
                <button
                  key={r.id}
                  onClick={() => assignRecipe(r)}
                  className="w-full text-left p-2 rounded hover:bg-white/5 border border-white/5 text-sm text-white flex justify-between items-center"
                  data-testid={`fe-admin-manual-pick-${r.id}`}
                >
                  <span className="truncate">{r.name}</span>
                  <span className="text-xs text-white/60 shrink-0 ml-2">
                    {Math.round(r.calories || 0)} kcal · {r.meal_slot}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
